import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../service/firebase";
import { addJogador, addJogo, clearAllData } from "../../../db/db";
import { createTurmaJogo } from "../../../service/jogoTurmaService";
import "./TurmaNovoJogo.css";

const TurmaNovoJogo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [turma, setTurma] = useState(null);
  const [integrantes, setIntegrantes] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const getNomePreferencial = (usuario, fallback = "Jogador") => {
    if (!usuario) return fallback;
    return (
      usuario.nickname ||
      usuario.displayName ||
      usuario.nome ||
      usuario.email ||
      fallback
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      setLoading(true);
      setError("");
      try {
        const turmaRef = await getDoc(doc(db, "turmas", id));
        if (!turmaRef.exists()) {
          setError("Turma não encontrada.");
          setLoading(false);
          return;
        }
        const turmaData = { id, ...turmaRef.data() };
        setTurma(turmaData);

        const membrosSnapshot = await getDocs(
          query(
            collection(db, "jogador_turma"),
            where("id_turma", "==", id),
            where("status", "==", 1)
          )
        );

        const membros = await Promise.all(
          membrosSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            try {
              const usuarioDoc = await getDoc(
                doc(db, "usuarios", data.id_usuario)
              );
              const usuarioData = usuarioDoc.exists()
                ? usuarioDoc.data()
                : null;
              const overallUsuario =
                typeof usuarioData?.overall === "number" ? usuarioData.overall : 65;
              const overallTurma =
                typeof data.overall === "number" ? data.overall : overallUsuario;
              return {
                membershipId: docSnapshot.id,
                id_usuario: data.id_usuario,
                admin: data.admin,
                status: data.status,
                usuario: usuarioData,
                overallTurma,
                overallGlobal: overallUsuario,
              };
            } catch (usuarioErr) {
              console.error("Erro ao carregar usuário", usuarioErr);
              return {
                membershipId: docSnapshot.id,
                id_usuario: data.id_usuario,
                admin: data.admin,
                status: data.status,
                usuario: null,
                overallTurma: 65,
                overallGlobal: 65,
              };
            }
          })
        );

        setIntegrantes(membros);
        const currentMember = membros.find(
          (membro) => membro.id_usuario === user.uid
        );
        setIsAdmin(Boolean(currentMember?.admin));
      } catch (err) {
        console.error("Erro ao carregar dados da turma", err);
        setError("Não foi possível carregar os dados desta turma.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user]);

  const participantesMinimos = useMemo(() => {
    if (!turma) return 0;
    return Math.max(2, (turma.qtd_jogadores_time || 1) * 2);
  }, [turma]);
  const turmaStatus = typeof turma?.status === "number" ? turma.status : 1;
  const turmaAtiva = turmaStatus === 1;

  const handleAdicionarIntegrante = (membro) => {
    if (!turmaAtiva || !isAdmin) {
      return;
    }
    if (!membro || participantes.some((p) => p.membershipId === membro.membershipId)) {
      return;
    }
    const nome = getNomePreferencial(membro.usuario, "Jogador");
    const overallGlobal =
      typeof membro.overallGlobal === "number" ? membro.overallGlobal : 65;
    const overallTurma =
      typeof membro.overallTurma === "number" ? membro.overallTurma : overallGlobal;
    const novoParticipante = {
      id: `membro-${membro.membershipId}`,
      nome,
      email: membro.usuario?.email || "",
      id_usuario: membro.id_usuario,
      membershipId: membro.membershipId,
      isGuest: false,
      overallGlobal,
      overallTurma,
    };
    setParticipantes((prev) => [...prev, novoParticipante]);
  };

  const handleAdicionarConvidado = () => {
    if (!turmaAtiva || !isAdmin) {
      return;
    }
    if (!guestName.trim()) {
      setError("Informe o apelido do convidado.");
      return;
    }
    setError("");
    const convidado = {
      id: `guest-${Date.now()}`,
      nome: guestName.trim(),
      email: "",
      id_usuario: null,
      membershipId: null,
      isGuest: true,
      overallGlobal: 65,
      overallTurma: 65,
    };
    setParticipantes((prev) => [...prev, convidado]);
    setGuestName("");
  };

  const handleRemoverParticipante = (participanteId) => {
    setParticipantes((prev) => prev.filter((item) => item.id !== participanteId));
  };

  const handleAvancar = async () => {
    if (!isAdmin) {
      setError("Apenas administradores podem iniciar um novo jogo.");
      return;
    }
    if (!turmaAtiva) {
      setError("Esta turma está inativa ou encerrada e não pode iniciar novos jogos.");
      return;
    }
    if (participantes.length < participantesMinimos) {
      alert(
        `Inclua pelo menos ${participantesMinimos} jogadores para iniciar o jogo.`
      );
      return;
    }
    setError("");
    setProcessing(true);
    try {
      const participantesUsuarioIds = participantes
        .filter((p) => !p.isGuest && p.id_usuario)
        .map((p) => p.id_usuario);

      const jogoTurmaId = await createTurmaJogo({
        turmaId: id,
        participantesIds: participantesUsuarioIds,
        createdBy: user?.uid,
      });

      await clearAllData();

      await addJogo({
        id: `turma-${jogoTurmaId}`,
        nome: turma.nome,
        tipo: "turma",
        contexto: "turma",
        turmaId: id,
        turmaNome: turma.nome,
        jogoTurmaId,
        jogadoresPorTime: turma.qtd_jogadores_time,
        tempoDeJogo: turma.tempo_de_partida,
        pontosParaAcabar: turma.max_pontos_partida,
        dadosIndividuais: turma.dados_individuais,
        corTime1: turma.cor_time_1 || "#000000",
        corTime2: turma.cor_time_2 || "#8cff53",
        maxVictories: turma.max_vitorias || 0,
      });

      let contador = 1;
      for (const participante of participantes) {
        await addJogador({
          id: contador,
          id_fixo: contador,
          nome: participante.nome,
          email: participante.email || "",
          id_usuario: participante.id_usuario || null,
          id_membro_turma: participante.membershipId || null,
          is_convidado: participante.isGuest || false,
          gols: 0,
          assistencias: 0,
          vitorias: 0,
          empates: 0,
          derrotas: 0,
          qt_partidas: 0,
          jogosSemTomarGol: 0,
          influenciaVitoria: 0,
          desempenhoAcumulado: 0,
          overallGlobal:
            typeof participante.overallGlobal === "number"
              ? participante.overallGlobal
              : 65,
          overallTurma:
            typeof participante.overallTurma === "number"
              ? participante.overallTurma
              : typeof participante.overallGlobal === "number"
                ? participante.overallGlobal
                : 65,
        });
        contador += 1;
      }

      navigate(`/turmas/${id}/jogos/${jogoTurmaId}/pre-jogo`);
    } catch (err) {
      console.error("Erro ao iniciar jogo da turma", err);
      setError(err.message || "Não foi possível iniciar o novo jogo.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card turma-novo-jogo-card">
            <div className="topo-page">
              <strong># Turma | Novo Jogo</strong>
            </div>
            {loading ? (
              <span>Carregando...</span>
            ) : error ? (
              <p className="turma-error" role="alert">
                {error}
              </p>
            ) : (
              <>
                {!isAdmin && (
                  <p className="turma-error">
                    Apenas administradores podem iniciar um novo jogo nesta turma.
                  </p>
                )}
                {isAdmin && !turmaAtiva && (
                  <p className="turma-error">
                    Esta turma está {turmaStatus === 2 ? "encerrada" : "inativa"} e não permite criar novos jogos.
                  </p>
                )}
                <div className="turma-novo-jogo-info">
                  <p>
                    <strong>Turma:</strong> {turma?.nome}
                  </p>
                  <p>
                    <strong>Jogadores por time:</strong>{" "}
                    {turma?.qtd_jogadores_time}
                  </p>
                  <p>
                    <strong>Tempo de partida:</strong>{" "}
                    {turma?.tempo_de_partida} min
                  </p>
                </div>

                <div className="turma-integrantes-section">
                  <h3>Integrantes da turma</h3>
                  {integrantes.length === 0 ? (
                    <p className="turma-participantes-empty">
                      Nenhum integrante ativo foi encontrado.
                    </p>
                  ) : (
                    <ul className="turma-integrantes-list">
                      {integrantes.map((membro) => {
                        const nome = getNomePreferencial(membro.usuario, "Jogador");
                        const selecionado = participantes.some(
                          (p) => p.membershipId === membro.membershipId
                        );
                        return (
                          <li key={membro.membershipId}>
                            <div>
                              <strong>{nome}</strong>
                              {/* <small>{membro.usuario?.email}</small> */}
                            </div>
                            <button
                              type="button"
                              className={`turma-add-player-btn ${
                                selecionado ? "selected" : ""
                              }`}
                              disabled={selecionado || !turmaAtiva || !isAdmin}
                              onClick={() => handleAdicionarIntegrante(membro)}
                              aria-label={
                                selecionado
                                  ? `${nome} já está no jogo`
                                  : `Incluir ${nome} neste jogo`
                              }
                              title={
                                selecionado
                                  ? `${nome} já está no jogo`
                                  : `Incluir ${nome} neste jogo`
                              }
                            >
                              <span aria-hidden="true">
                                {selecionado ? "✓" : "+"}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="turma-convidado-section">
                  <h3>Adicionar convidado</h3>
                  <div className="turma-convidado-form">
                    <input
                      type="text"
                      placeholder="Apelido do convidado"
                      value={guestName}
                      onChange={(event) => setGuestName(event.target.value)}
                      disabled={!turmaAtiva || !isAdmin}
                    />
                    <button
                      type="button"
                      className="btn-default"
                      onClick={handleAdicionarConvidado}
                      disabled={!turmaAtiva || !isAdmin}
                    >
                      Adicionar convidado
                    </button>
                  </div>
                </div>

                <div className="turma-participantes-section">
                  <h3>Participantes selecionados ({participantes.length})</h3>
                  {participantes.length === 0 ? (
                    <p className="turma-participantes-empty">
                      Adicione integrantes da turma ou convide convidados para participar deste jogo.
                    </p>
                  ) : (
                    <ul className="turma-participantes-list">
                      {participantes.map((participante, index) => (
                        <li key={participante.id}>
                          <div>
                            <span className="turma-participante-index">
                              {index + 1}.
                            </span>{" "}
                            <strong>{participante.nome}</strong>
                            {participante.isGuest && (
                              <span className="turma-chip convidado">
                                Convidado
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="turma-remove-participante"
                            onClick={() =>
                              handleRemoverParticipante(participante.id)
                            }
                          >
                            -
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="turma-limite-info">
                    É necessário incluir pelo menos {participantesMinimos}{" "}
                    jogadores antes de avançar.
                  </div>
                </div>

                <div className="turma-avancar-section">
                  <button
                    type="button"
                    className="btn-default"
                    onClick={handleAvancar}
                    disabled={!isAdmin || !turmaAtiva || processing}
                  >
                    {processing ? "Preparando..." : "Avançar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TurmaNovoJogo;
